import JsonLd from "@/components/JsonLd.tsx";
import JsonLdBreadcrumbList from "@/components/JsonLdBreadcrumbList.tsx";
import {config} from "@/config/config.ts";
import FaqComponent from "@/landing/sections/FaqComponent.tsx";
import {createFileRoute, Link} from '@tanstack/react-router'
import LandingNav from "../landing/components/LandingNav.tsx";
import ArrowUpRightIcon from "../landing/icons/ArrowUpRightIcon.tsx";
import CheckBadgeIcon from "../landing/icons/CheckBadgeIcon.tsx";
import FooterSection from "../landing/sections/FooterSection.tsx";

export const Route = createFileRoute('/football-league-software')({
    component: RouteComponent,
})

const FEATURES = [
    {code: "FX", title: "Home & away fixtures", desc: "Generate full season fixture lists across divisions, pitches and match days."},
    {code: "PT", title: "3-1-0 league tables", desc: "Standings calculated automatically from wins, draws and losses."},
    {code: "GD", title: "Goals & goal difference", desc: "Track goals for, goals against and goal difference on every table."},
    {code: "SC", title: "Result reporting", desc: "Managers submit scores after the final whistle and tables update."},
    {code: "GS", title: "Goalscorers", desc: "Record who scored and build a running top-scorer chart for the season."},
    {code: "AG", title: "Divisions & age groups", desc: "Run first team, reserves, youth age groups and small-sided leagues side by side."},
];

const LEAGUE_TYPES = [
    {name: "League Season", sub: "Home and away, everyone plays everyone", to: "/round-robin-tournament-generator/"},
    {name: "League & Cup", sub: "Group stage into knockout rounds", to: "/group-and-knockout-generator/"},
    {name: "Football Tournaments", sub: "Cups, groups and knockout brackets", to: "/football-tournament-software/"},
    {name: "Sports League Management", sub: "Recurring fixtures and standings", to: "/sports-league-management-software/"},
];

const BENEFITS = [
    {title: "Run a full season", desc: "Plan match weeks across divisions, teams, pitches and kick-off times."},
    {title: "Tables that maintain themselves", desc: "Points, goal difference and form update the moment a result is in."},
    {title: "Keep everyone informed", desc: "Players, parents and supporters follow fixtures and tables from a public link."},
];

const USE_CASES = [
    {title: "For Grassroots Clubs", desc: "Run local league seasons without spreadsheets or printed tables."},
    {title: "For Youth Leagues", desc: "Manage age groups from under-8s upwards, each with its own division."},
    {title: "For 5-a-side & Small Sided", desc: "Organise midweek small-sided leagues across multiple pitches."},
    {title: "For Sunday Leagues", desc: "Fixtures, results, tables and discipline records in one place."},
    {title: "For Workplace Leagues", desc: "Keep company and social leagues running with minimal admin."},
    {title: "For Academies", desc: "Track internal leagues, development fixtures and player minutes."},
];

const COMPARE = [
    {need: "Fixture lists", manual: "Rebuilt by hand each season", scorepal: "Generated across divisions and match weeks"},
    {need: "League tables", manual: "Formulas that break when a result changes", scorepal: "Points, goal difference and form calculated automatically"},
    {need: "Result collection", manual: "WhatsApp messages and missed scorelines", scorepal: "Managers submit results against the fixture"},
    {need: "Goalscorers", manual: "Tracked separately, if at all", scorepal: "Recorded per match and totalled for the season"},
    {need: "Supporter experience", manual: "Tables shared as screenshots", scorepal: "A live public page for fixtures, results and standings"},
];

const FAQS = [
    {
        q: 'What is football league software?',
        a: <>Football league software helps clubs and organisers build season fixture lists, collect results, calculate league tables and publish standings for every division without maintaining spreadsheets.</>,
    },
    {
        q: 'Does ScorePal calculate league tables automatically?',
        a: <>Yes. Football tables use the standard three points for a win and one for a draw, with goals for, goals against and goal difference worked out from the scores as they are entered.</>,
    },
    {
        q: 'Can ScorePal run youth age groups and multiple divisions?',
        a: <>Yes. Each age group or division runs as its own competition with its own fixtures and table, so a club can manage first team, reserves and youth football side by side.</>,
    },
    {
        q: 'Can supporters follow the league?',
        a: <>Yes. Every league has a public live page showing fixtures, results and the current table, so players, parents and supporters can follow along without an account.</>,
    },
    {
        q: 'Is this different from football tournament software?',
        a: <>Yes. Football league software is built for a season of recurring fixtures and a table. <Link to={"/football-tournament-software/" as any} className="text-[#082F8A] underline font-semibold">Football tournament software</Link> is built for cups and one-day events with group stages and knockout rounds.</>,
    },
];

function RouteComponent() {
    return (
        <div className="min-h-screen bg-[#EDEEF4]">
            <title>Football League Software for Clubs & Organisers | ScorePal</title>
            <meta name="description" content="Run football leagues with ScorePal. Generate home and away fixtures, collect results, track goalscorers and publish automatic league tables with goal difference for every division." />
            <link rel="canonical" href={`${config.publicUrl}/football-league-software/`} />
            <meta property="og:title" content="Football League Software | ScorePal" />
            <meta property="og:description" content="Create football league fixtures, collect results and publish automatic league tables with ScorePal." />
            <meta property="og:type" content="website" />
            <meta property="og:url" content={`${config.publicUrl}/football-league-software/`} />
            <meta property="og:image" content="https://scorepal.co/images/football-league-software-og.jpg" />
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content="Football League Software | ScorePal" />
            <meta name="twitter:description" content="Run football league seasons, fixtures, results and tables from one platform." />

            <JsonLd id={`page${Route.id}/app`} item={{
                "@context": "https://schema.org",
                "@type": "SoftwareApplication",
                "name": "ScorePal Football League Software",
                "applicationCategory": "SportsApplication",
                "operatingSystem": "Web",
                "description": "Football league software for managing season fixtures, divisions, results, goalscorers and automatic league tables.",
                "url": "https://scorepal.co/football-league-software/",
                "publisher": {"@type": "Organization", "name": "ScorePal"},
            }} />
            <JsonLd id={`page${Route.id}/faq-schema`} item={{
                "@context": "https://schema.org",
                "@type": "FAQPage",
                "mainEntity": [
                    {"@type": "Question", "name": "What is football league software?", "acceptedAnswer": {"@type": "Answer", "text": "Football league software helps clubs and organisers build season fixture lists, collect results, calculate league tables and publish standings for every division without maintaining spreadsheets."}},
                    {"@type": "Question", "name": "Does ScorePal calculate league tables automatically?", "acceptedAnswer": {"@type": "Answer", "text": "Yes. Football tables use the standard three points for a win and one for a draw, with goals for, goals against and goal difference worked out from the scores as they are entered."}},
                    {"@type": "Question", "name": "Can ScorePal run youth age groups and multiple divisions?", "acceptedAnswer": {"@type": "Answer", "text": "Yes. Each age group or division runs as its own competition with its own fixtures and table, so a club can manage first team, reserves and youth football side by side."}},
                    {"@type": "Question", "name": "Can supporters follow the league?", "acceptedAnswer": {"@type": "Answer", "text": "Yes. Every league has a public live page showing fixtures, results and the current table, so players, parents and supporters can follow along without an account."}},
                    {"@type": "Question", "name": "Is this different from football tournament software?", "acceptedAnswer": {"@type": "Answer", "text": "Yes. Football league software is built for a season of recurring fixtures and a table, while football tournament software is built for cups and one-day events with group stages and knockout rounds."}},
                ],
            }} />
            <JsonLdBreadcrumbList routeId={Route.id} crumbs={[{name: "Football League Software", item: "https://scorepal.co/football-league-software/"}]} />

            {/* Hero */}
            <section className="relative overflow-hidden p-4">
                <div className="rounded-4xl bg-linear-to-br from-[#0a1530] via-[#0d1b3d] to-[#082F8A] pb-12">
                    <div className="relative z-20">
                        <LandingNav />
                    </div>

                    <div className="container mx-auto px-6 lg:px-12 pt-8">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
                            <div>
                                <span className="inline-flex items-center rounded-full bg-white/10 px-4 py-2 text-sm font-semibold text-white/90 mb-5">
                                    Football League Software
                                </span>
                                <h1 className="font-bebas text-white uppercase text-[clamp(38px,5.5vw,68px)] leading-none font-bold tracking-wide mb-5">
                                    Run a football season without rebuilding the table every week
                                </h1>
                                <p className="font-inter text-white/85 text-base lg:text-xl max-w-2xl leading-relaxed mb-8">
                                    ScorePal helps football clubs and league organisers generate home and away fixtures, collect
                                    results, record goalscorers and publish league tables that update themselves — across every
                                    division and age group.
                                </p>
                                <div className="flex flex-wrap items-center gap-3">
                                    <Link to={"/signup/player/" as any} className="inline-flex items-center gap-2 bg-[#EDEEF4] text-[#082F8A] rounded-full pl-6 pr-1 py-1 font-semibold hover:bg-white transition">
                                        <span className="font-inter text-base font-medium">Get started - it's free</span>
                                        <div className="bg-[#082F8A] rounded-full p-1">
                                            <ArrowUpRightIcon className="text-white" size={28} />
                                        </div>
                                    </Link>
                                    <Link to={"/football-tournament-software/" as any} className="rounded-full border border-white/40 px-6 py-3 text-base font-semibold text-white transition hover:bg-white/10">
                                        Running a Cup?
                                    </Link>
                                </div>
                            </div>

                            {/* Live league table card */}
                            <div className="bg-white/5 border border-white/10 rounded-3xl p-6 lg:p-8 backdrop-blur-sm">
                                <h3 className="font-bebas text-white text-2xl font-bold tracking-wide mb-2">Live football league table</h3>
                                <p className="font-inter text-white/70 text-sm mb-5">Points, goal difference and position update as results come in on match day.</p>
                                <div className="rounded-2xl bg-white/10 p-3">
                                    <div className="grid grid-cols-[28px_1fr_36px_44px] items-center gap-2 pb-2 border-b border-white/15 font-inter text-white/50 text-xs uppercase tracking-wide">
                                        <span>#</span><span>Team</span><span className="text-center">GD</span><span className="text-right">Pts</span>
                                    </div>
                                    {[
                                        {pos: 1, team: "Northside FC", gd: "+18", pts: "34"},
                                        {pos: 2, team: "Riverside United", gd: "+11", pts: "31"},
                                        {pos: 3, team: "Athletic Rovers", gd: "+4", pts: "27"},
                                        {pos: 4, team: "Parkside Town", gd: "-2", pts: "22"},
                                    ].map((row, i, arr) => (
                                        <div key={row.pos} className={`grid grid-cols-[28px_1fr_36px_44px] items-center gap-2 py-3 ${i < arr.length - 1 ? "border-b border-white/10" : ""}`}>
                                            <span className="font-inter text-white/60 text-sm">{row.pos}</span>
                                            <strong className="font-inter text-white text-sm">{row.team}</strong>
                                            <span className="font-inter text-white/70 text-sm text-center">{row.gd}</span>
                                            <strong className="font-inter text-white text-sm text-right">{row.pts}</strong>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Features */}
            <section id="features" className="py-16 lg:py-24 bg-white scroll-mt-24">
                <div className="container mx-auto px-6 lg:px-12">
                    <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-3">
                        Everything you need to run a football league
                    </h2>
                    <p className="font-inter text-[#1a1a2e]/70 text-base max-w-2xl mb-10">
                        Replace the spreadsheet table, the group chat scorelines and the printed fixture list with software built for a football season.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {FEATURES.map((f) => (
                            <div key={f.title} className="bg-white border border-[#e5e7eb] rounded-2xl p-6 shadow-sm">
                                <div className="w-11 h-11 rounded-xl bg-[#EDEEF4] text-[#082F8A] flex items-center justify-center font-bold mb-4">{f.code}</div>
                                <h3 className="font-inter text-[#1a1a2e] font-semibold text-lg mb-2">{f.title}</h3>
                                <p className="font-inter text-[#1a1a2e]/70 text-sm leading-relaxed">{f.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* League types */}
            <section id="league-types" className="py-16 lg:py-24 bg-[#EDEEF4] scroll-mt-24">
                <div className="container mx-auto px-6 lg:px-12">
                    <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-3">
                        Support for every type of football competition
                    </h2>
                    <p className="font-inter text-[#1a1a2e]/70 text-base max-w-2xl mb-10">
                        Whether it is a full home and away season, a league that finishes with a cup, or a midweek small-sided competition, ScorePal handles the structure.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {LEAGUE_TYPES.map((f) => (
                            <Link key={f.name} to={f.to as any} className="group bg-white border border-[#e5e7eb] rounded-2xl p-5 hover:-translate-y-1 hover:border-[#082F8A]/25 transition">
                                <div className="font-inter font-bold text-[#1a1a2e] group-hover:text-[#082F8A]">{f.name}</div>
                                <div className="font-inter text-[#1a1a2e]/60 text-sm mt-1">{f.sub}</div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* Season management + mock app */}
            <section className="py-16 lg:py-24 bg-white">
                <div className="container mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
                    <div>
                        <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-4">
                            Built for the rhythm of a football season
                        </h2>
                        <p className="font-inter text-[#1a1a2e]/70 text-base leading-relaxed mb-6">
                            A league is more than a fixture list. Managers need to know who they are playing and where,
                            players want to see the table on Sunday evening, and organisers need results in without chasing.
                        </p>
                        <div className="space-y-4">
                            {BENEFITS.map((b) => (
                                <div key={b.title} className="flex gap-3">
                                    <CheckBadgeIcon size={24} />
                                    <div>
                                        <strong className="font-inter text-[#1a1a2e]">{b.title}</strong>
                                        <p className="font-inter text-[#1a1a2e]/70 text-sm">{b.desc}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Mock app */}
                    <div className="rounded-3xl bg-linear-to-br from-[#eef4ff] to-white border border-[#e5e7eb] p-6 lg:p-8 shadow-sm">
                        <div className="bg-white border border-[#e5e7eb] rounded-2xl p-5">
                            {[
                                {l: "Division One — Match Week 14", r: "In progress", badge: "live"},
                                {l: "Next fixture", r: "Northside FC vs Parkside Town"},
                                {l: "Kick-off", r: "Sunday 10:30 · Pitch 2"},
                                {l: "Results in", r: "84 / 132 matches"},
                                {l: "Top scorer", r: "J. Okafor — 14 goals", badge: "gold"},
                            ].map((row, i, arr) => (
                                <div key={row.l} className={`flex items-center justify-between gap-3 py-3.5 font-inter text-sm ${i < arr.length - 1 ? "border-b border-[#eef0f4]" : ""}`}>
                                    <span className="text-[#1a1a2e]/70">{row.l}</span>
                                    {row.badge === "live"
                                        ? <span className="rounded-full bg-[#ecfdf3] text-[#12b76a] px-2.5 py-1 text-xs font-bold">{row.r}</span>
                                        : row.badge === "gold"
                                            ? <span className="rounded-full bg-[#fffbeb] text-[#b54708] px-2.5 py-1 text-xs font-bold">{row.r}</span>
                                            : <strong className="text-[#1a1a2e] text-right">{row.r}</strong>}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Comparison */}
            <section id="comparison" className="py-16 lg:py-24 bg-[#EDEEF4] scroll-mt-24">
                <div className="container mx-auto px-6 lg:px-12">
                    <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-3">
                        ScorePal vs spreadsheets for football leagues
                    </h2>
                    <p className="font-inter text-[#1a1a2e]/70 text-base max-w-2xl mb-8">
                        A spreadsheet copes until a result changes, a match is postponed or a second division is added — then the formulas start fighting back.
                    </p>
                    <div className="overflow-x-auto">
                        <div className="min-w-[680px] border border-[#d9dce7] rounded-3xl overflow-hidden bg-white shadow-sm">
                            <div className="grid grid-cols-3 bg-[#F6F7FB] font-bebas text-lg">
                                <div className="p-5 border-r border-[#d9dce7] text-[#1a1a2e]">Need</div>
                                <div className="p-5 border-r border-[#d9dce7] text-[#1a1a2e]">Spreadsheets</div>
                                <div className="p-5 text-[#082F8A]">ScorePal</div>
                            </div>
                            {COMPARE.map((row, i) => (
                                <div key={row.need} className={`grid grid-cols-3 border-t border-[#d9dce7] font-inter text-[#1a1a2e] ${i % 2 ? "bg-[#FAFBFD]" : ""}`}>
                                    <div className="p-5 border-r border-[#d9dce7] font-semibold">{row.need}</div>
                                    <div className="p-5 border-r border-[#d9dce7] text-[#1a1a2e]/70">{row.manual}</div>
                                    <div className="p-5 font-medium">{row.scorepal}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Use cases */}
            <section className="py-16 lg:py-24 bg-white">
                <div className="container mx-auto px-6 lg:px-12">
                    <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-3">
                        Built for clubs, youth football and local leagues
                    </h2>
                    <p className="font-inter text-[#1a1a2e]/70 text-base max-w-2xl mb-10">
                        From an eleven-a-side Sunday league to a midweek five-a-side competition, ScorePal keeps the fixtures and the table in order.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {USE_CASES.map((u) => (
                            <div key={u.title} className="bg-white border border-[#e5e7eb] rounded-2xl p-6 shadow-sm">
                                <h3 className="font-inter text-[#082F8A] font-semibold text-base mb-2">{u.title}</h3>
                                <p className="font-inter text-[#1a1a2e]/70 text-sm leading-relaxed">{u.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA card → tournament software */}
            <section className="container mx-auto px-6 lg:px-12 py-8 lg:py-12">
                <div className="rounded-3xl bg-linear-to-br from-[#0a1530] via-[#0d1b3d] to-[#082F8A] p-8 lg:p-12 grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-8 lg:gap-12 items-center">
                    <div>
                        <h2 className="font-bebas text-white uppercase text-[clamp(26px,3.5vw,40px)] leading-tight font-bold tracking-wide mb-4">
                            Running a cup or a one-day football tournament?
                        </h2>
                        <p className="font-inter text-white/80 text-base leading-relaxed mb-6">
                            This page is about league seasons. If you are running a cup competition, a group stage into
                            knockout rounds or a one-day festival, see our football tournament software page.
                        </p>
                        <div className="flex flex-wrap gap-3">
                            <Link to={"/football-tournament-software/" as any} className="inline-flex items-center gap-2 bg-white text-[#082F8A] rounded-full pl-6 pr-1 py-1 font-semibold hover:bg-gray-100 transition">
                                <span className="font-inter text-base font-medium">View Tournament Software</span>
                                <div className="bg-[#082F8A] rounded-full p-1">
                                    <ArrowUpRightIcon className="text-white" size={28} />
                                </div>
                            </Link>
                            <Link to={"/signup/player/" as any} className="rounded-full border border-white/40 px-6 py-3 text-base font-semibold text-white transition hover:bg-white/10">
                                Get started - it's free
                            </Link>
                        </div>
                    </div>
                    <div className="bg-white/10 rounded-2xl p-6">
                        <h3 className="font-inter text-white font-semibold mb-3">Popular cup features</h3>
                        <ul className="list-disc pl-5 space-y-1.5 font-inter text-sm text-white/85">
                            <li>Group stages</li>
                            <li>Knockout brackets</li>
                            <li>Third place play-offs</li>
                            <li>Age group divisions</li>
                            <li>Finals day scheduling</li>
                        </ul>
                    </div>
                </div>
            </section>

            {/* Final CTA */}
            <section className="container mx-auto px-6 lg:px-12 pb-8 lg:pb-12">
                <div className="bg-[#082F8A] rounded-3xl px-8 py-16 lg:px-16 lg:py-20 text-center">
                    <h2 className="font-bebas text-white uppercase text-[clamp(32px,5vw,52px)] leading-tight font-bold tracking-wide mb-6">
                        Run your next football league with ScorePal
                    </h2>
                    <p className="font-inter text-white/90 text-base lg:text-lg leading-relaxed max-w-3xl mx-auto mb-6">
                        Season fixtures, results, goalscorers, goal difference and automatic league tables — football league software built for clubs and organisers.
                    </p>
                    <div className="flex flex-wrap justify-center gap-2.5 mb-10">
                        {["Home & Away Fixtures", "League Tables", "Goal Difference", "Goalscorers", "Divisions", "Live Results"].map((pill) => (
                            <span key={pill} className="rounded-full bg-white/10 px-4 py-2 font-inter text-sm font-medium text-white/90">{pill}</span>
                        ))}
                    </div>
                    <div className="flex justify-center">
                        <Link to={"/signup/player/" as any} className="inline-flex items-center gap-2 bg-white text-[#082F8A] rounded-full pl-6 pr-1 py-1 font-semibold hover:bg-gray-100 transition">
                            <span className="font-inter text-base font-medium">Get started - it's free</span>
                            <div className="bg-[#082F8A] rounded-full p-1">
                                <ArrowUpRightIcon className="text-white" size={28} />
                            </div>
                        </Link>
                    </div>
                </div>
            </section>

            <FaqComponent title={"Football League Software FAQs"} faqs={FAQS} />
            <FooterSection />
        </div>
    );
}
